import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updatePositionTitle } from '@/api/positionTitles';
import type { PositionTitle } from '@/types/oscar';

/** TanStack Query key for the full position-title list (shared with the board). */
export const TITLES_KEY = ['positionTitles'] as const;

/**
 * Re-home mutation — moves a title to a different department, optimistically
 * (TanStack Query `onMutate` + rollback on error), then invalidates the title
 * list. Lives in its own module (not the component file) so it can be unit-
 * tested directly — dnd-kit's drag gesture is wired in the component, but the
 * persistence logic (optimistic update + rollback) is proven here without
 * simulating a pixel-accurate drag.
 */
export function useRehomeTitle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, toCode }: { id: number; toCode: string }) =>
      updatePositionTitle(id, { DepartmentCode: toCode }),
    onMutate: async ({ id, toCode }) => {
      await qc.cancelQueries({ queryKey: TITLES_KEY });
      const prev = qc.getQueryData<PositionTitle[]>(TITLES_KEY);
      qc.setQueryData<PositionTitle[]>(TITLES_KEY, (old) =>
        (old ?? []).map((t) => (t.PositionTitleId === id ? { ...t, DepartmentCode: toCode } : t)));
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(TITLES_KEY, ctx.prev); // roll the chip back
    },
    onSettled: () => qc.invalidateQueries({ queryKey: TITLES_KEY }),
  });
}
